import {
	Dispatch,
	useMemo
} from "react";
import { MenuItemKind } from "./GlobalNavigationSidebar";
import { isNil, isNotNil } from "client/Misc/utils";
import createVanilla from "zustand/vanilla";
import { persist } from "zustand/middleware";
import { IsolatedLocalStorageStateStorage } from "client/zustand/persistence";
import create, { UseStore } from "zustand";
import { defineInitialVersion, withMigrator } from "client/zustand/migrations";

export function useSubNavigationWidthState(itemKind: MenuItemKind): [
	width: number | null,
	onChangeWidth: Dispatch<number | null>
];
export function useSubNavigationWidthState(itemKind: MenuItemKind | undefined): [
	width: number | null,
	onChangeWidth: Dispatch<number | null> | undefined
];
export function useSubNavigationWidthState(itemKind: MenuItemKind | undefined): [
	width: number | null,
	onChangeWidth: Dispatch<number | null> | undefined
] {
	const {
		items,
		setWidth
	} = useSubNavigationWidthStore();

	const width: number | null = items.find(i => i.itemKind === itemKind)
		?.width
			?? null;

	const onChangeWidth = useMemo(
		(): ((newWidth: number | null) => void) | undefined => {
			if (isNil(itemKind)) {
				return undefined;
			}

			return (newWidth: number | null): void => {
				setWidth(itemKind, newWidth);
			};
		},
		[itemKind, setWidth]
	);

	return [
		width,
		onChangeWidth
	];
}

type ItemKindWidthState = {
	itemKind: MenuItemKind;
	width: number;
};

type SubNavigationWidthStoreData = {
	items: readonly ItemKindWidthState[];
	setWidth: (itemKind: MenuItemKind, width: number | null) => void;
};

const subNavigationWidthStore = createVanilla<SubNavigationWidthStoreData>(
	persist(
		(set): SubNavigationWidthStoreData => {
			return {
				items: [],
				setWidth(itemKind: MenuItemKind, width: number | null): void {
					set((previousState) => {
						const previousItems: readonly ItemKindWidthState[] = previousState.items;

						const items: ItemKindWidthState[] = previousItems.filter(i => i.itemKind !== itemKind);

						if (isNotNil(width)) {
							items.push({
								itemKind,
								width
							});
						}

						return {
							items
						};
					});
				}
			};
		},
		withMigrator(
			{
				name: "SubNavigationWidthState",
				getStorage: () => new IsolatedLocalStorageStateStorage()
			},
			defineInitialVersion(1)
		)
	)
);

const useSubNavigationWidthStore: UseStore<SubNavigationWidthStoreData> = create(subNavigationWidthStore);